import { createFileRoute, Link } from "@tanstack/react-router";
import { inspectorImg } from "@/lib/inspector-image";
import homeWatchHero from "@/assets/home-watch-hero.webp.asset.json";
import storm from "@/assets/storm.jpg";
import { Section, SectionHeading, Eyebrow, CheckList, FaqAccordion, TrustBar, CtaBand } from "@/components/sections";
import { QuoteForm } from "@/components/QuoteForm";
import { faqJsonLd, serviceJsonLd, type FaqItem, SERVICE_AREAS, breadcrumbJsonLd } from "@/lib/site";
import { Calendar, Home, ShieldCheck, Wind, Droplets, Wrench, Eye, Bug, ShieldAlert, Mail as MailIcon, ClipboardCheck, MessageSquare } from "lucide-react";

const faqs: FaqItem[] = [
  { q: "What's the difference between Home Watch and a property manager?", a: "Property managers handle renters and rental income. Home Watch is for owners who aren't renting — we monitor your unoccupied home for problems and coordinate care, but we don't manage tenants." },
  { q: "What does a typical visit include?", a: "A complete interior and exterior walkthrough using a consistent checklist — looking for water, pest, HVAC, electrical and security issues — followed by a detailed photo report sent the same day." },
  { q: "How often should I have my home watched?", a: "Most insurance carriers and our experience recommend at least every 7 days during storm season and every 14 days otherwise. We'll recommend a schedule based on your home." },
  { q: "Will you let in cleaners, contractors and deliveries?", a: "Yes. Concierge access is included with most plans — we'll meet vendors, accept deliveries and re-secure the home when they leave." },
  { q: "How fast do you respond to a problem?", a: "If we find something urgent during a visit, you'll know within the hour. We can also dispatch trusted local plumbers, HVAC techs and remediation companies on your behalf." },
  { q: "Is Home Watch a substitute for an alarm system?", a: "No — they complement each other. Alarms detect break-ins after the fact. Home Watch is your weekly set of eyes catching the leaks, mold, pests and storm damage that alarms simply don't see." },
  { q: "How much does Home Watch cost in Sarasota?", a: "Most Sarasota homeowners pay between $55 and $110 per visit depending on property size, visit frequency and whether you add concierge services like vendor access or storm prep. Weekly visits are the most cost-effective per-inspection, while monthly plans work well for smaller homes or short absences." },
  { q: "Are there setup fees or contracts?", a: "No setup fees and no long-term contracts. We do an initial walkthrough to document your home's systems and your preferences, then you can pause, change frequency or cancel with 14 days' notice." },
];

export const Route = createFileRoute("/home-watch-services")({
  head: () => ({
    meta: [
      { title: "Sarasota Home Watch Services | Anchor Services" },
      { name: "description", content: "Professional Home Watch in Sarasota: weekly, bi-weekly and monthly inspections of unoccupied homes. Leak, HVAC, storm and pest monitoring with detailed photo reports." },
      { property: "og:title", content: "Sarasota Home Watch Services" },
      { property: "og:description", content: "Weekly, bi-weekly and monthly Home Watch inspections across the Sarasota coast — with detailed photo reports after every visit." },
      { property: "og:url", content: "https://anchorsrq.com/home-watch-services" },
    ],
    links: [{ rel: "canonical", href: "https://anchorsrq.com/home-watch-services" }, { rel: "preload", as: "image", href: homeWatchHero.url, fetchpriority: "high" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd([{ name: 'Home', url: 'https://anchorsrq.com/' }, { name: 'Services', url: 'https://anchorsrq.com/#services' }, { name: 'Home Watch Services', url: 'https://anchorsrq.com/home-watch-services' }])) },
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd({ slug: "home-watch-services", name: 'Home Watch Services', serviceType: 'Home Watch', description: "Home Watch from Anchor Services for Sarasota-area homeowners — scheduled weekly, bi-weekly, or monthly inspections of unoccupied homes with same-day photo reports covering leaks, HVAC, pest activity, storm prep, and security." })) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
    ],
  }),
  component: HomeWatchPage,
});

function HomeWatchPage() {
  return (
    <>
      <section className="relative isolate overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 -z-10">
          <img src={homeWatchHero.url} alt="" width={1600} height={912} fetchPriority="high" decoding="async" className="h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.22_0.04_250)]/90 to-[oklch(0.22_0.04_250)]/65" />
        </div>
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="max-w-2xl">
            <Eyebrow><span className="text-[oklch(0.85_0.12_82)]">Home Watch</span></Eyebrow>
            <h1 className="font-serif text-4xl leading-tight sm:text-5xl lg:text-6xl">
              Sarasota Home Watch Services
            </h1>
            <p className="mt-5 text-base text-primary-foreground/85 sm:text-lg">
              Weekly, bi-weekly and monthly inspections for snowbirds, seasonal residents and second-home owners — with a detailed photo report after every single visit.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" className="rounded-md bg-[oklch(0.78_0.12_82)] px-5 py-3 text-sm font-semibold text-[oklch(0.22_0.04_250)] hover:brightness-105">Get a Free Quote</Link>
              <a href="tel:+19415002320" className="rounded-md border border-primary-foreground/30 px-5 py-3 text-sm font-medium hover:bg-primary-foreground/10">Call (941) 500-2320</a>
            </div>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* Why Home Watch */}
      <Section>
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Why Home Watch Matters</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">An empty home is a vulnerable home.</h2>
            <p className="mt-4 text-muted-foreground">
              Florida's heat, humidity, salt air and storms work overtime when you're gone. A slow leak, a tripped breaker, a failed HVAC or a pest intrusion can turn into tens of thousands of dollars in damage before anyone notices. Anchor Services is the eyes and ears on your property — so the home you love is exactly the way you left it when you return.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Catch leaks, mold and HVAC failures early — before they spread.",
                "Document storm prep and post-storm condition with photos.",
                "Meet contractors, accept deliveries and check the mail.",
                "Detailed inspection reports after every visit — so you always know.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-foreground/85">
                  <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-[oklch(0.78_0.12_82)]" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={inspectorImg.src} srcSet={inspectorImg.srcSet} sizes={inspectorImg.sizes} alt="Anchor Services inspector reviewing a tablet at a Sarasota home" width={inspectorImg.width} height={inspectorImg.height} loading="lazy" decoding="async" className="aspect-[16/9] w-full rounded-2xl object-cover shadow-lg" />
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-border bg-card p-4 shadow-md sm:block">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Reports Delivered</div>
              <div className="font-serif text-2xl text-primary">After every visit</div>
            </div>
          </div>
        </div>
      </Section>

      {/* What every visit covers */}
      <Section tone="muted">
        <SectionHeading
          eyebrow="Home Watch Services"
          title="A complete look at your Sarasota property — every visit."
          intro="Choose weekly, bi-weekly or monthly inspections, plus storm season add-ons. Each visit includes a full interior and exterior walkthrough."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Eye, t: "Visual Interior & Exterior", d: "Top-to-bottom walkthrough looking for any signs of trouble." },
            { icon: Droplets, t: "Leak Detection", d: "Plumbing, fixtures, ceilings, water heater and under-sink checks." },
            { icon: Wind, t: "HVAC Monitoring", d: "Verify cooling, humidity and air handler performance." },
            { icon: Bug, t: "Pest Activity Checks", d: "Watch for ants, roaches, rodents and other early signs." },
            { icon: ShieldAlert, t: "Storm Prep & Post-Storm", d: "Shutters, patio furniture, then post-storm condition reports." },
            { icon: MailIcon, t: "Mail & Package Checks", d: "Pick up mail, accept packages and keep your entry clear." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="rounded-xl border border-border bg-card p-6">
              <Icon className="h-6 w-6 text-[oklch(0.55_0.12_82)]" />
              <div className="mt-4 font-serif text-lg text-foreground">{t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Inspection process */}
      <Section>
        <SectionHeading eyebrow="Our Process" title="How an Anchor Services inspection works" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { i: Home, t: "1. Onboarding", d: "We walk the property with you, document systems and confirm preferences." },
            { i: Calendar, t: "2. Scheduled Visits", d: "We arrive on schedule — weekly, bi-weekly or monthly." },
            { i: ClipboardCheck, t: "3. Detailed Inspection", d: "A consistent checklist covers interior, exterior and systems." },
            { i: MessageSquare, t: "4. Photo Report", d: "You get a written report with photos after every single visit." },
          ].map(({ i: Icon, t, d }) => (
            <div key={t} className="relative rounded-xl border border-border bg-card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[oklch(0.78_0.12_82)]/20 text-[oklch(0.55_0.12_82)]">
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-serif text-lg">{t}</div>
              <p className="mt-1.5 text-sm text-muted-foreground">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow="Plans & Pricing"
          title="Choose the visit cadence that fits your home."
          intro="Transparent pricing for Sarasota homeowners. Most clients pick weekly or bi-weekly inspections, with custom add-ons for storm season, contractor access and pre-arrival prep."
        />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {[
            { t: "Weekly Inspections", range: "$55–$85 per visit", d: "Recommended for coastal homes, condo associations requiring weekly checks and homes in active storm season.", best: "Highest protection" },
            { t: "Bi-Weekly Inspections", range: "$65–$95 per visit", d: "Our most popular plan — a thorough inspection every 14 days, all year round.", best: "Most popular" },
            { t: "Monthly Inspections", range: "$75–$110 per visit", d: "Ideal for newer homes with minimal systems, short absences or supplemental coverage.", best: "Most economical" },
          ].map((p) => (
            <div key={p.t} className="rounded-xl border border-border bg-card p-7">
              <div className="text-xs font-semibold uppercase tracking-wider text-[oklch(0.55_0.12_82)]">{p.best}</div>
              <div className="mt-2 font-serif text-2xl text-primary">{p.t}</div>
              <div className="mt-3 font-semibold text-foreground">{p.range}</div>
              <p className="mt-3 text-sm text-muted-foreground">{p.d}</p>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground">
          Typical ranges for single-family homes in Sarasota. Condos and estates may differ.{" "}
          <Link to="/contact" className="text-primary underline underline-offset-4">Request a custom quote</Link> for your exact property.
        </p>
      </Section>

      <Section>
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>What affects your price</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">Three variables that change your cost</h2>
            <p className="mt-4 text-muted-foreground">
              No two homes are alike. Here's how we calculate a fair, custom quote for every Sarasota property we watch.
            </p>
            <div className="mt-8 space-y-6">
              {[
                { icon: Calendar, title: "Visit Frequency", desc: "Weekly visits cost less per inspection than monthly because we build efficiency into the schedule. Bi-weekly strikes the best balance for most owners." },
                { icon: Home, title: "Property Size & Systems", desc: "A 3-bedroom ranch with a pool and lanai takes longer to inspect than a 2-bedroom condo. More square footage, more systems, more points to check." },
                { icon: Wrench, title: "Add-On Services", desc: "Storm prep, contractor access, pre-arrival grocery stocking, HVAC filter changes — each adds time but saves you from hiring separate vendors." },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[oklch(0.78_0.12_82)]/20 text-[oklch(0.55_0.12_82)]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium text-foreground">{title}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-6">
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-primary">Flat monthly plans</div>
              <p className="mt-3 text-sm text-muted-foreground">
                Prefer predictable billing? Many clients choose a flat monthly rate that bundles inspections, storm response and concierge access. No per-visit math, no surprise charges — just a set-it-and-forget-it monthly fee.
              </p>
              <div className="mt-4 text-sm font-medium text-foreground">Contact us for a custom flat-rate proposal.</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-primary">No contracts, no setup fees</div>
              <p className="mt-3 text-sm text-muted-foreground">
                We don't lock you in. Pause your service when you're in town, change your frequency when storm season starts, or cancel with 14 days' notice. Your home, your schedule.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Return on investment"
          title="The real cost is not hiring Home Watch"
          intro="Home Watch isn't an expense — it's insurance against the kind of damage that destroys budgets. Here's what a missed problem can cost in Sarasota."
        />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Droplets, title: "Undetected leak", catch: "$200–$500", miss: "$8,000–$25,000+", desc: "A slow drip under a sink caught early is a quick plumber call. Left for months, it destroys cabinetry, flooring, drywall and can trigger mold remediation." },
            { icon: Wind, title: "HVAC failure in summer", catch: "$150–$400", miss: "$3,000–$7,000", desc: "Florida heat and humidity wreck an unconditioned home in days. Warped wood, mold, spoiled wine, ruined electronics — all preventable with early detection." },
            { icon: ShieldCheck, title: "Storm damage unseen", catch: "$0 (just a report)", miss: "$5,000–$50,000+", desc: "A lifted shingle or cracked window after a storm lets water in for weeks. A post-storm inspection documents issues the same day so you can file a timely claim." },
          ].map(({ icon: Icon, title, catch: catchCost, miss, desc }) => (
            <div key={title} className="rounded-xl border border-border bg-card p-6">
              <Icon className="h-6 w-6 text-[oklch(0.55_0.12_82)]" />
              <div className="mt-4 font-serif text-lg text-primary">{title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
              <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-secondary/40 p-3">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[oklch(0.55_0.12_82)]">Caught early</div>
                  <div className="mt-0.5 text-sm font-semibold text-foreground">{catchCost}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-red-500">Missed</div>
                  <div className="mt-0.5 text-sm font-semibold text-red-600">{miss}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-3xl rounded-xl border border-[oklch(0.78_0.12_82)]/30 bg-[oklch(0.78_0.12_82)]/10 p-6 text-center">
          <p className="text-sm font-medium text-foreground">
            At $55–$85 per visit, a single prevented leak or HVAC failure pays for an entire year of Home Watch inspections.
          </p>
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading
          eyebrow="Competitive comparison"
          title="Why homeowners choose Anchor Services"
          intro="We're not the cheapest option — and we don't try to be. Here's how we stack up against the competition."
        />
        <div className="mx-auto mt-10 max-w-3xl overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr>
                <th className="px-5 py-3 text-left font-medium text-foreground">Feature</th>
                <th className="px-5 py-3 text-center font-medium text-primary">Anchor Services</th>
                <th className="px-5 py-3 text-center font-medium text-muted-foreground">Typical competitor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {[
                { f: "Same-day photo report", a: "Included every visit", c: "Often delayed or extra fee" },
                { f: "Storm season add-ons", a: "Included in most plans", c: "Extra charge or not offered" },
                { f: "Concierge / vendor access", a: "Included", c: "Separate service or hourly rate" },
                { f: "Background-checked staff", a: "Yes — every inspector", c: "Not always guaranteed" },
                { f: "Licensed, insured & bonded", a: "Full coverage", c: "Varies — verify before hiring" },
                { f: "Contract required", a: "No — cancel anytime", c: "Often 6–12 month minimum" },
                { f: "Setup fee", a: "None", c: "$50–$150 common" },
              ].map((row) => (
                <tr key={row.f}>
                  <td className="px-5 py-3 text-foreground/90">{row.f}</td>
                  <td className="px-5 py-3 text-center font-medium text-primary">{row.a}</td>
                  <td className="px-5 py-3 text-center text-muted-foreground">{row.c}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section tone="muted">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>What we look for</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">Our complete inspection checklist</h2>
            <p className="mt-4 text-muted-foreground">
              Every visit follows the same proven checklist — so nothing slips through the cracks, no matter who's on duty.
            </p>
            <div className="mt-8"><CheckList items={[
              "Interior visual walkthrough — every room",
              "Ceilings, walls and floors for water signs",
              "Plumbing fixtures, under-sink and water heater",
              "Refrigerator, freezer and ice maker check",
              "HVAC operation, thermostat, humidity readings",
              "Air handler, filter and condensate line",
              "Doors, windows and locks — interior and exterior",
              "Pest activity: ants, roaches, rodents, droppings",
              "Electrical panel and visible wiring",
              "Smoke and CO detectors operational",
              "Garage and shed checks",
              "Exterior walkaround — roof, gutters, siding",
              "Landscaping, pool deck and lanai condition",
              "Mail, packages and visible signage",
              "Storm shutters and outdoor furniture (in season)",
              "Vehicle check, if left on property",
            ]} /></div>
          </div>
          <div className="space-y-6">
            <img src={storm} alt="Storm preparation at a Florida home" width={1600} height={1000} loading="lazy" className="rounded-2xl object-cover shadow-lg" />
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-primary">Storm season add-ons</div>
              <p className="mt-3 text-sm text-muted-foreground">
                Pre-storm shutter close-ups, patio furniture stowing, pool-cage prep, post-storm condition reports and insurance documentation — included when you need it.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Reporting" title="A photo report after every single visit." intro="Detailed, time-stamped, with notes and recommendations — delivered the same day so you always know how your home is doing." />
        <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-3">
          {[
            { t: "Same-day delivery", d: "Reports land in your inbox within hours of the visit." },
            { t: "Photo documentation", d: "Wide and detail shots of everything we checked." },
            { t: "Clear recommendations", d: "If something needs attention, we tell you what and why." },
          ].map((r) => (
            <div key={r.t} className="rounded-xl border border-border bg-card p-6 text-center">
              <div className="font-serif text-lg text-primary">{r.t}</div>
              <p className="mt-2 text-sm text-muted-foreground">{r.d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="muted">
        <SectionHeading eyebrow="Service area" title="Serving the entire Sarasota coast" />
        <div className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-2">
          {SERVICE_AREAS.map((a) => (
            <Link key={a} to="/service-areas" className="rounded-full border border-border bg-card px-4 py-1.5 text-sm text-foreground/80 hover:border-[oklch(0.78_0.12_82)]">
              {a}
            </Link>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="FAQ" title="Home Watch questions, answered" />
        <div className="mt-10"><FaqAccordion items={faqs} /></div>
      </Section>

      <Section tone="muted">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>Get Started</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl">Request a Home Watch quote.</h2>
            <p className="mt-4 text-muted-foreground">
              Share a few details and we'll send a custom proposal — typically within one business day.
            </p>
          </div>
          <QuoteForm sourcePage="/home-watch-services" defaultService="Home Watch" />
        </div>
      </Section>

      <CtaBand title="Your Sarasota home deserves a watchful eye." />
    </>
  );
}